/**
 * @fileoverview motion.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
/**
 * motion.js - Reusable Framer Motion variants & helper components for KITH Travels.
 *
 * Usage:
 *   import { FadeUp, FadeIn, SlideIn, StaggerContainer, ScaleIn } from '../utils/motion'
 *
 *   <FadeUp><h2>Hello</h2></FadeUp>
 *   <StaggerContainer><FadeUp>...</FadeUp><FadeUp>...</FadeUp></StaggerContainer>
 */

import { motion } from 'framer-motion';

/* ─── Base variants ────────────────────────────────────────────────────── */

export const fadeUpVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
    },
};

export const fadeInVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { duration: 0.6, ease: 'easeOut' },
    },
};

export const slideInLeftVariants = {
    hidden: { opacity: 0, x: -60 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    },
};

export const slideInRightVariants = {
    hidden: { opacity: 0, x: 60 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    },
};

export const scaleInVariants = {
    hidden: { opacity: 0, scale: 0.92 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
    },
};

export const staggerContainerVariants = {
    hidden: {},
    visible: {
        transition: {
            staggerChildren: 0.12,
            delayChildren: 0.05,
        },
    },
};

/* ─── Scroll-triggered wrapper components ──────────────────────────────── */

const viewportOptions = { once: true, amount: 0.2 };

/** Fades and slides up from below - ideal for headings, paragraphs, CTAs */
export function FadeUp({ children, delay = 0, className = '' }) {
    return (
        <motion.div
            className={className}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOptions}
            variants={{
                hidden: { opacity: 0, y: 40 },
                visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay },
                },
            }}
        >
            {children}
        </motion.div>
    );
}

/** Simple opacity fade - ideal for images, backgrounds */
export function FadeIn({ children, delay = 0, className = '' }) {
    return (
        <motion.div
            className={className}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOptions}
            variants={{
                hidden: { opacity: 0 },
                visible: {
                    opacity: 1,
                    transition: { duration: 0.8, ease: 'easeOut', delay },
                },
            }}
        >
            {children}
        </motion.div>
    );
}

/** Slides in from the left */
export function SlideInLeft({ children, delay = 0, className = '' }) {
    return (
        <motion.div
            className={className}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOptions}
            variants={{
                hidden: { opacity: 0, x: -60 },
                visible: {
                    opacity: 1,
                    x: 0,
                    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay },
                },
            }}
        >
            {children}
        </motion.div>
    );
}

/** Slides in from the right */
export function SlideInRight({ children, delay = 0, className = '' }) {
    return (
        <motion.div
            className={className}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOptions}
            variants={{
                hidden: { opacity: 0, x: 60 },
                visible: {
                    opacity: 1,
                    x: 0,
                    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay },
                },
            }}
        >
            {children}
        </motion.div>
    );
}

/** Scales up from slightly smaller - ideal for cards */
export function ScaleIn({ children, delay = 0, className = '' }) {
    return (
        <motion.div
            className={className}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOptions}
            variants={{
                hidden: { opacity: 0, scale: 0.92 },
                visible: {
                    opacity: 1,
                    scale: 1,
                    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay },
                },
            }}
        >
            {children}
        </motion.div>
    );
}

/**
 * Wraps children and staggers their entrance animations.
 * Each child must use the motion variants (e.g. fadeUpVariants).
 */
export function StaggerContainer({ children, className = '' }) {
    return (
        <motion.div
            className={className}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOptions}
            variants={staggerContainerVariants}
        >
            {children}
        </motion.div>
    );
}

/** A pre-built staggered child - used inside StaggerContainer */
export function StaggerItem({ children, className = '' }) {
    return (
        <motion.div className={className} variants={fadeUpVariants}>
            {children}
        </motion.div>
    );
}

/* ─── Page transition wrapper ───────────────────────────────────────────── */

/** Wraps an entire page with a fade-in transition */
export function PageTransition({ children }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.div>
    );
}
